import { logEvent, setUserProperties } from 'firebase/analytics';

/**
 * Reusable Analytics Service for tracking quiz events
 *
 * Usage:
 *   import { analyticsService } from './services/analyticsService';
 *   analyticsService.trackQuizStarted(sessionId);
 *
 * Reusability:
 *   This service can be reused across different quizzes/apps by changing the appName config.
 *   Example: new AnalyticsService(analytics, { appName: 'love_quiz' });
 */
class AnalyticsService {
  constructor(analyticsInstance, config = {}) {
    this.analytics = analyticsInstance;
    this.appName = config.appName || 'jaimee_quiz';
    this.debug = config.debug || false;
  }

  /**
   * Initialize with analytics instance
   * Call this after Firebase is initialized
   */
  init(analyticsInstance) {
    this.analytics = analyticsInstance;
  }

  /**
   * Private helper - tracks event with app prefix and enriched params
   */
  _track(eventName, params = {}) {
    const fullEventName = `${this.appName}_${eventName}`;
    const enrichedParams = {
      ...params,
      app_name: this.appName,
      timestamp: new Date().toISOString(),
    };

    if (this.debug) {
      console.log(`[Analytics] ${fullEventName}`, enrichedParams);
    }

    if (this.analytics) {
      try {
        logEvent(this.analytics, fullEventName, enrichedParams);
      } catch (error) {
        console.error('[Analytics] Error logging event:', error);
      }
    }
  }

  /**
   * Private helper - sets user property for segmentation
   */
  _setUserProperty(name, value) {
    if (this.analytics) {
      try {
        setUserProperties(this.analytics, { [name]: value });
      } catch (error) {
        console.error('[Analytics] Error setting user property:', error);
      }
    }
  }

  // ==================== QUIZ FUNNEL EVENTS ====================

  /**
   * Track when quiz starts (first question displayed)
   * @param {string} sessionId - Quiz session ID
   */
  trackQuizStarted(sessionId) {
    this._track('started', { session_id: sessionId });
  }

  /**
   * Track when user answers a question
   * @param {Object} data - Question answer data
   * @param {string} data.sessionId - Quiz session ID
   * @param {string} data.questionId - Question ID
   * @param {number} data.questionOrder - Question order (1-based)
   * @param {string} data.questionType - Question type (single-select, multi-select, slider)
   * @param {number} data.timeSpentSeconds - Time spent on question
   */
  trackQuestionAnswered(data) {
    this._track('question_answered', {
      session_id: data.sessionId,
      question_id: data.questionId,
      question_order: data.questionOrder,
      question_type: data.questionType,
      time_spent_seconds: data.timeSpentSeconds,
    });
  }

  /**
   * Track when user reaches a quiz milestone (25%, 50%, 75%)
   * @param {Object} data - Milestone data
   * @param {string} data.sessionId - Quiz session ID
   * @param {string} data.milestone - Milestone reached ('25%', '50%', '75%')
   * @param {number} data.questionOrder - Question number when milestone was reached
   */
  trackMilestoneReached(data) {
    this._track('milestone_reached', {
      session_id: data.sessionId,
      milestone: data.milestone,
      question_order: data.questionOrder,
    });
  }

  /**
   * Track when quiz is completed (all questions answered)
   * @param {Object} data - Completion data
   * @param {string} data.sessionId - Quiz session ID
   * @param {number} data.totalTimeSeconds - Total time spent on quiz
   * @param {number} data.questionsAnswered - Number of questions answered
   */
  trackQuizCompleted(data) {
    this._track('completed', {
      session_id: data.sessionId,
      total_time_seconds: data.totalTimeSeconds,
      questions_answered: data.questionsAnswered,
    });
    this._setUserProperty('quiz_completed', 'true');
  }

  /**
   * Track when user abandons quiz mid-way
   * @param {Object} data - Abandonment data
   * @param {string} data.sessionId - Quiz session ID
   * @param {number} data.lastQuestionOrder - Last question answered
   * @param {number} data.percentComplete - Percentage of quiz completed
   * @param {number} data.timeSpentSeconds - Time spent before abandoning
   */
  trackQuizAbandoned(data) {
    this._track('abandoned', {
      session_id: data.sessionId,
      last_question_order: data.lastQuestionOrder,
      percent_complete: data.percentComplete,
      time_spent_seconds: data.timeSpentSeconds,
    });
  }

  // ==================== CONVERSION EVENTS ====================

  /**
   * Track when user submits email
   * @param {Object} data - Email capture data
   * @param {string} data.sessionId - Quiz session ID
   * @param {string} data.email - User's email (domain extracted for privacy)
   */
  trackEmailCaptured(data) {
    const emailDomain = data.email ? data.email.split('@')[1] : 'unknown';
    this._track('email_captured', {
      session_id: data.sessionId,
      email_domain: emailDomain,
    });
    this._setUserProperty('email_captured', 'true');
  }

  /**
   * Track when email validation fails
   * @param {string} errorType - Type of error ('format' or 'dns')
   */
  trackEmailValidationFailed(errorType) {
    this._track('email_validation_failed', {
      error_type: errorType,
    });
  }

  /**
   * Track when results page is viewed
   * @param {Object} data - Results view data
   * @param {string} data.sessionId - Quiz session ID
   * @param {string} data.personalityType - User's personality type result
   */
  trackResultsViewed(data) {
    this._track('results_viewed', {
      session_id: data.sessionId,
      personality_type: data.personalityType,
    });
    this._setUserProperty('personality_type', data.personalityType);
  }

  /**
   * Track when user initiates sharing
   * @param {string} method - Share method ('copy_link', 'download', 'native')
   */
  trackShareInitiated(method) {
    this._track('share_initiated', {
      share_method: method,
    });
  }

  /**
   * Track when share action completes
   * @param {Object} data - Share completion data
   * @param {string} data.method - Share method
   * @param {string} data.platform - Platform shared to (optional)
   */
  trackShareCompleted(data) {
    this._track('share_completed', {
      share_method: data.method,
      platform: data.platform || 'unknown',
    });
  }

  /**
   * Track when user clicks app download link
   * @param {Object} data - App download data
   * @param {string} data.platform - Platform ('ios', 'android')
   * @param {string} data.source - Where the click came from ('results_page', 'email', 'share')
   */
  trackAppDownloadClicked(data) {
    this._track('app_download_clicked', {
      platform: data.platform,
      source: data.source,
    });
  }

  // ==================== ENGAGEMENT EVENTS ====================

  /**
   * Track when tarot card is flipped
   * @param {string} personalityType - User's personality type
   */
  trackTarotCardFlipped(personalityType) {
    this._track('tarot_card_flipped', {
      personality_type: personalityType,
    });
  }

  /**
   * Track when user likes their tarot card result
   * @param {string} personalityType - User's personality type
   */
  trackTarotCardLiked(personalityType) {
    this._track('tarot_card_liked', {
      personality_type: personalityType,
    });
  }

  /**
   * Track when user views a specific result section
   * @param {string} section - Section name ('summary', 'strengths', 'growth', 'compatibility')
   */
  trackResultSectionViewed(section) {
    this._track('result_section_viewed', {
      section: section,
    });
  }

  // ==================== ERROR EVENTS ====================

  /**
   * Track quiz errors
   * @param {Object} data - Error data
   * @param {string} data.errorType - Type of error
   * @param {string} data.errorMessage - Error message
   * @param {number} data.questionOrder - Question order where error occurred (optional)
   */
  trackError(data) {
    this._track('error', {
      error_type: data.errorType,
      error_message: data.errorMessage,
      question_order: data.questionOrder || null,
    });
  }

  /**
   * Track API errors
   * @param {Object} data - API error data
   * @param {string} data.endpoint - API endpoint
   * @param {number} data.statusCode - HTTP status code
   * @param {string} data.errorMessage - Error message
   */
  trackApiError(data) {
    this._track('api_error', {
      endpoint: data.endpoint,
      status_code: data.statusCode,
      error_message: data.errorMessage,
    });
  }

  // ==================== UTM TRACKING ====================

  /**
   * Capture UTM parameters from URL and track as event + user properties
   * Call this on initial page load
   * @returns {Object} - Captured UTM parameters
   */
  captureUtmParams() {
    if (typeof window === 'undefined') return {};

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_content: urlParams.get('utm_content'),
      utm_term: urlParams.get('utm_term'),
    };

    // Filter out null values
    const filteredParams = Object.fromEntries(
      Object.entries(utmParams).filter(([_, v]) => v != null)
    );

    if (Object.keys(filteredParams).length > 0) {
      this._track('campaign_captured', filteredParams);

      // Also set as user properties for segmentation
      Object.entries(filteredParams).forEach(([key, value]) => {
        this._setUserProperty(key, value);
      });
    }

    return filteredParams;
  }
}

// Create singleton instance (will be initialized in App.js)
const analyticsService = new AnalyticsService(null, {
  appName: 'personality_quiz',
  debug: process.env.NODE_ENV === 'development',
});

export default AnalyticsService;
export { analyticsService };
